// Shared types + formatting helpers for the 3M Consolidation Report.
// gRPC can omit zero-valued fields, so every reader goes through normalizeRollup
// / num and defaults missing values to 0 / false.

export interface ThreeMRollupRaw {
  materialAmount?: string;
  materialLineCount?: number;
  manpowerAmount?: string;
  manpowerLineCount?: number;
  machineryAmount?: string;
  machineryLineCount?: number;
  totalAmount?: string;
  allottedQuantity?: string;
  requestedQuantity?: string;
  orderedQuantity?: string;
  issuedQuantity?: string;
  utilizedQuantity?: string;
  normLineCount?: number;
  hasNorm?: boolean;
}

export interface ThreeMRollup {
  materialAmount: number;
  materialLineCount: number;
  manpowerAmount: number;
  manpowerLineCount: number;
  machineryAmount: number;
  machineryLineCount: number;
  totalAmount: number;
  allottedQuantity: number;
  requestedQuantity: number;
  orderedQuantity: number;
  issuedQuantity: number;
  utilizedQuantity: number;
  normLineCount: number;
  hasNorm: boolean;
}

export interface ThreeMTaskNode {
  taskId: number;
  taskName: string;
  taskSequenceId?: string;
  taskStatus?: string;
  workQuantity?: string;
  workUom?: string;
  /** tasks.uploadRate — client rate per unit of work, net of GST and labour
   *  cess (the PRICE side, on the same tax basis as the 3M cost columns). */
  rate?: string;
  completionPercentage?: number | string;
  rollup?: ThreeMRollupRaw;
}

export interface ThreeMActivityNode {
  activityId: number;
  activityName: string;
  activityStatus?: string;
  rollup?: ThreeMRollupRaw;
  tasks?: ThreeMTaskNode[];
}

export interface ThreeMPhaseNode {
  phaseId: number;
  phaseName: string;
  phaseStatus?: string;
  rollup?: ThreeMRollupRaw;
  activities?: ThreeMActivityNode[];
}

export interface ThreeMSummary {
  totals?: ThreeMRollupRaw;
  phaseCount?: number;
  activityCount?: number;
  taskCount?: number;
  tasksWithNorms?: number;
}

export interface ThreeMConsolidationData {
  projectName?: string;
  summary?: ThreeMSummary;
  phases?: ThreeMPhaseNode[];
}

// Safe number: "" / undefined / null / NaN → 0
export const num = (value: any): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const normalizeRollup = (
  r?: ThreeMRollupRaw | null
): ThreeMRollup => ({
  materialAmount: num(r?.materialAmount),
  materialLineCount: num(r?.materialLineCount),
  manpowerAmount: num(r?.manpowerAmount),
  manpowerLineCount: num(r?.manpowerLineCount),
  machineryAmount: num(r?.machineryAmount),
  machineryLineCount: num(r?.machineryLineCount),
  totalAmount: num(r?.totalAmount),
  allottedQuantity: num(r?.allottedQuantity),
  requestedQuantity: num(r?.requestedQuantity),
  orderedQuantity: num(r?.orderedQuantity),
  issuedQuantity: num(r?.issuedQuantity),
  utilizedQuantity: num(r?.utilizedQuantity),
  normLineCount: num(r?.normLineCount),
  hasNorm: !!r?.hasNorm,
});

// ₹ 12,34,567.89 — Indian grouping, always 2 decimals
export const fmtINR = (value: any): string =>
  `₹${num(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Quantity: en-IN grouping, up to 4 decimals, trailing zeros trimmed
export const fmtQty = (value: any): string =>
  num(value).toLocaleString("en-IN", { maximumFractionDigits: 4 });

// Pareto / ABC classification over cost-ranked rows (A ≈ first 80% of spend,
// B → 95%, C = the long tail). Shared by the Material & Manpower Cost Analysis
// charts and Excel exports. Input must already be sorted by cost DESC; each
// point spreads the original item so callers keep their extra fields.
export type ParetoPoint<T> = T & {
  rank: number;
  cumCost: number;
  // Rounded to 2dp BEFORE threshold checks so 79.999 doesn't misclassify
  cumPct: number;
};

export interface ParetoAbc<T> {
  points: ParetoPoint<T>[];
  aCount: number;
  bCount: number;
  cCount: number;
  classOf: (rank: number) => "A" | "B" | "C";
}

export const computeParetoAbc = <T extends { cost: number }>(
  ranked: T[]
): ParetoAbc<T> => {
  const total = ranked.reduce((sum, m) => sum + m.cost, 0);
  let running = 0;
  const points = ranked.map((m, i) => {
    running += m.cost;
    const cumPct = total > 0 ? (running / total) * 100 : 0;
    return {
      ...m,
      rank: i + 1,
      cumCost: running,
      cumPct: Math.round(cumPct * 100) / 100,
    };
  });
  const aIndex = points.findIndex((p) => p.cumPct >= 80);
  const aCount =
    points.length === 0 ? 0 : aIndex === -1 ? points.length : aIndex + 1;
  const bIndex = points.findIndex((p) => p.cumPct >= 95);
  const bEnd = bIndex === -1 ? points.length : bIndex + 1;
  const bCount = Math.max(0, bEnd - aCount);
  const cCount = Math.max(0, points.length - aCount - bCount);
  const classOf = (rank: number): "A" | "B" | "C" =>
    rank <= aCount ? "A" : rank <= aCount + bCount ? "B" : "C";
  return { points, aCount, bCount, cCount, classOf };
};

// Compact Indian notation: ₹3.09 Cr / ₹12.35 L / ₹45.2 K, plain ₹ below 1,000.
// Pair with a Tooltip carrying fmtINR for the exact value.
export const fmtINRCompact = (value: any): string => {
  const n = num(value);
  const abs = Math.abs(n);
  const scaled = (div: number, suffix: string) =>
    `₹${(n / div).toLocaleString("en-IN", { maximumFractionDigits: 2 })} ${suffix}`;
  if (abs >= 1e7) return scaled(1e7, "Cr");
  if (abs >= 1e5) return scaled(1e5, "L");
  if (abs >= 1e3) return scaled(1e3, "K");
  return fmtINR(n);
};

// ---- Tree building (shared by ConsolidationTree + PlannedVsActualTree) ----

// Scales hardcoded px sizes by the header's A / A+ / A++ preference.
export type FsFn = (px: number) => number;

// Same muted status palette as tasks/GridView.tsx
export const getStatusColor = (status: string): string => {
  if (!status) return "default";
  const statusLower = status.toLowerCase().replace(/\s+/g, "");
  const statusColorMap: { [key: string]: string } = {
    open: "cyan",
    inprogress: "blue",
    underqc: "purple",
    onhold: "warning",
    closed: "red",
    active: "green",
    inactive: "default",
    draft: "magenta",
    pendingapproval: "gold",
    approvalrejected: "volcano",
    archived: "geekblue",
    deleted: "lime",
  };
  return statusColorMap[statusLower] || "default";
};

export interface TreeRow {
  key: string;
  id: number;
  name: string;
  status?: string;
  sequenceId?: string;
  workQuantity?: string;
  workUom?: string;
  /** BOQ rate per unit — tasks only; parents derive theirs from boqValue ÷ qty. */
  rate?: string;
  /**
   * BOQ value = Σ (work quantity × rate) over this node's tasks. Rupees sum
   * across UOMs, so unlike workQuantity this stays meaningful on a mixed-UOM
   * parent — which is why it is a separate field and not derived on render.
   */
  boqValue?: number;
  completionPercentage?: number | string;
  rollup: ThreeMRollup;
  children?: TreeRow[];
}

/** One task's BOQ value; 0 when either side is missing. */
export const taskBoqValue = (task: {
  workQuantity?: string;
  rate?: string;
}): number => num(task?.workQuantity) * num(task?.rate);

export const sumBoqValue = (
  tasks: Array<{ workQuantity?: string; rate?: string }>,
): number => (tasks || []).reduce((sum, task) => sum + taskBoqValue(task), 0);

// Aggregated work basis for a phase/activity: the sum of its tasks' work
// quantities — meaningful ONLY when every contributing task shares the same
// work UOM (a summed "m³ + rmt" figure would be nonsense, so omit it then).
export const aggWorkBasis = (
  tasks: Array<{ workQuantity?: string; workUom?: string }>,
): { workQuantity?: string; workUom?: string } => {
  let total = 0;
  const uoms = new Set<string>();
  (tasks || []).forEach((t) => {
    const qty = num(t.workQuantity);
    if (qty > 0) {
      total += qty;
      uoms.add((t.workUom || "").trim());
    }
  });
  if (total > 0 && uoms.size === 1) {
    return { workQuantity: String(total), workUom: Array.from(uoms)[0] };
  }
  return {};
};

export const buildTree = (phases: ThreeMPhaseNode[]): TreeRow[] =>
  (phases || []).map((phase) => ({
    key: `P-${phase.phaseId}`,
    id: phase.phaseId,
    name: phase.phaseName,
    status: phase.phaseStatus,
    rollup: normalizeRollup(phase.rollup),
    ...aggWorkBasis(
      (phase.activities || []).flatMap((activity) => activity.tasks || []),
    ),
    boqValue: sumBoqValue(
      (phase.activities || []).flatMap((activity) => activity.tasks || []),
    ),
    children: (phase.activities || []).map((activity) => ({
      key: `A-${activity.activityId}`,
      id: activity.activityId,
      name: activity.activityName,
      status: activity.activityStatus,
      rollup: normalizeRollup(activity.rollup),
      ...aggWorkBasis(activity.tasks || []),
      boqValue: sumBoqValue(activity.tasks || []),
      children: (activity.tasks || []).map((task) => ({
        key: `T-${task.taskId}`,
        id: task.taskId,
        name: task.taskName,
        status: task.taskStatus,
        sequenceId: task.taskSequenceId,
        workQuantity: task.workQuantity,
        workUom: task.workUom,
        rate: task.rate,
        boqValue: taskBoqValue(task),
        completionPercentage: task.completionPercentage,
        rollup: normalizeRollup(task.rollup),
      })),
    })),
  }));

export const threeMRowClassName = (record: { key?: string }): string => {
  const k = record?.key || "";
  if (k.startsWith("P-")) return "tasks-row-phase";
  if (k.startsWith("A-")) return "tasks-row-activity";
  if (k.startsWith("T-")) return "tasks-row-task";
  return "";
};

// ---- Planned vs Actual ----------------------------------------------------

// ACTUALS STUB / SEAM: actual spend capture (DPR / consumption / bills) is not
// wired yet. This is THE single place real actuals plug in later — resolve by
// node key ("P-<id>" / "A-<id>" / "T-<id>", "PRJ" = project total) and delete
// the zeros. Deviation math, cell colors and the Excel export all read through
// this function and already handle nonzero values.
export interface ThreeMActuals {
  materialAmount: number;
  manpowerAmount: number;
  machineryAmount: number;
  totalAmount: number;
}

export const getActualRollup = (_node: {
  key: string;
  id: number;
}): ThreeMActuals => ({
  materialAmount: 0,
  manpowerAmount: 0,
  machineryAmount: 0,
  totalAmount: 0,
});

export interface ThreeMDeviation {
  amount: number;
  pct: number;
}

// amount = actual − planned (negative ⇒ under/behind plan);
// pct = amount as a share of planned — NOT actual/planned progress.
// planned 0 ⇒ pct 0 (not NaN / −100).
export const computeDeviation = (
  planned: number,
  actual: number
): ThreeMDeviation => {
  const amount = actual - planned;
  return { amount, pct: planned > 0 ? (amount / planned) * 100 : 0 };
};

// Signed currency: formats |value| then prefixes the sign, because fmtINRCompact
// and fmtINR on a raw negative render the sign inside the symbol ("₹-3.09 Cr").
// U+2212 minus for on-screen display; zero stays unsigned.
export const fmtINRSigned = (value: any, full = false): string => {
  const n = num(value);
  const body = full ? fmtINR(Math.abs(n)) : fmtINRCompact(Math.abs(n));
  if (n === 0) return body;
  return `${n < 0 ? "−" : "+"}${body}`;
};

// "+12.3%" / "−100.0%" / "0.0%"
export const fmtDevPct = (pct: number): string =>
  `${pct > 0 ? "+" : pct < 0 ? "−" : ""}${Math.abs(pct).toFixed(1)}%`;

// ---------------------------------------------------------------------------
// Time phasing (the "Time Phasing" tab + the Start/Finish/Sched% columns)
// ---------------------------------------------------------------------------
// Cells are task-grain (task, month) money, from Get3MTimePhasing. Every 3M
// cost source is scaled by that month's share of the task
// (scheduledQty / workQuantity), the same math Monthly P&L uses.

export interface ThreeMTimePhasingCell {
  taskId: number;
  month: string; // 'YYYY-MM-01'
  materialAmount?: string;
  manpowerAmount?: string;
  machineryAmount?: string;
  /** The task's scheduled output that month — denominator for the rate. */
  scheduledQuantity?: string;
}

export interface ThreeMTimePhasingData {
  firstMonth?: string;
  lastMonth?: string;
  cellCount?: number;
  scheduledMaterial?: string;
  scheduledManpower?: string;
  scheduledMachinery?: string;
  cells?: ThreeMTimePhasingCell[];
}

export type ThreeMMeasure = "total" | "material" | "manpower" | "machinery";

export interface PhasedAmounts {
  material: number;
  manpower: number;
  machinery: number;
  total: number;
  // Scheduled output. Summed up the tree like the money, but only MEANINGFUL
  // where the child tasks share a UOM — callers must check the node's workUom
  // (buildTree leaves it empty on mixed-UOM parents) before dividing by it.
  quantity: number;
}

export interface PhasedNode {
  months: Map<string, PhasedAmounts>;
  start?: string;
  finish?: string;
  scheduled: PhasedAmounts;
}

export interface PhasingIndex {
  byKey: Map<string, PhasedNode>; // 'P-'/'A-'/'T-' keys + 'PRJ'
  allMonths: string[]; // ascending, every month present in the payload
}

const emptyAmounts = (): PhasedAmounts => ({
  material: 0,
  manpower: 0,
  machinery: 0,
  total: 0,
  quantity: 0,
});

const addAmounts = (target: PhasedAmounts, source: PhasedAmounts) => {
  target.material += source.material;
  target.manpower += source.manpower;
  target.machinery += source.machinery;
  target.total += source.total;
  target.quantity += source.quantity;
};

// Merge one node's month map into its parent's, creating months on demand.
const mergeMonths = (
  target: Map<string, PhasedAmounts>,
  source: Map<string, PhasedAmounts>,
) => {
  source.forEach((amounts, month) => {
    let slot = target.get(month);
    if (!slot) {
      slot = emptyAmounts();
      target.set(month, slot);
    }
    addAmounts(slot, amounts);
  });
};

const finalize = (months: Map<string, PhasedAmounts>): PhasedNode => {
  const scheduled = emptyAmounts();
  let start: string | undefined;
  let finish: string | undefined;
  months.forEach((amounts, month) => {
    addAmounts(scheduled, amounts);
    // 'YYYY-MM-01' compares lexically in chronological order.
    if (!start || month < start) start = month;
    if (!finish || month > finish) finish = month;
  });
  return { months, start, finish, scheduled };
};

// Index the flat cells against the WBS tree, once.
//
// Two passes, NOT a per-node filter: filtering cells per node would be
// O(nodes x cells) (~6.2M on project 112) and would re-run on every measure /
// FY switch. Pass 1 buckets cells by task; pass 2 walks the tree merging
// task -> activity -> phase -> project.
//
// Walking the TREE rather than grouping cells by phaseId is load-bearing: it
// guarantees a task filtered out of the tree can never leak money into a
// parent total, and it is why the cell payload carries no WBS ids at all.
//
// Memoise on [cells, phases] ONLY — never on the measure or the FY, so
// switching either is a pure re-render off this index.
export const buildPhasingIndex = (
  cells: ThreeMTimePhasingCell[] | undefined,
  phases: ThreeMPhaseNode[] | undefined,
): PhasingIndex => {
  const byTask = new Map<number, Map<string, PhasedAmounts>>();
  const monthSet = new Set<string>();

  (cells || []).forEach((cell) => {
    const taskId = num(cell.taskId);
    if (!taskId || !cell.month) return;
    let slot = byTask.get(taskId);
    if (!slot) {
      slot = new Map();
      byTask.set(taskId, slot);
    }
    const material = num(cell.materialAmount);
    const manpower = num(cell.manpowerAmount);
    const machinery = num(cell.machineryAmount);
    // total is summed client-side so the Total view equals the three per-M
    // views added up, to the paisa.
    slot.set(cell.month, {
      material,
      manpower,
      machinery,
      total: material + manpower + machinery,
      quantity: num(cell.scheduledQuantity),
    });
    monthSet.add(cell.month);
  });

  const byKey = new Map<string, PhasedNode>();
  const projectMonths = new Map<string, PhasedAmounts>();

  (phases || []).forEach((phase) => {
    const phaseMonths = new Map<string, PhasedAmounts>();
    (phase.activities || []).forEach((activity) => {
      const activityMonths = new Map<string, PhasedAmounts>();
      (activity.tasks || []).forEach((task) => {
        const taskMonths = byTask.get(num(task.taskId));
        if (!taskMonths) return;
        byKey.set(`T-${task.taskId}`, finalize(new Map(taskMonths)));
        mergeMonths(activityMonths, taskMonths);
      });
      byKey.set(`A-${activity.activityId}`, finalize(activityMonths));
      mergeMonths(phaseMonths, activityMonths);
    });
    byKey.set(`P-${phase.phaseId}`, finalize(phaseMonths));
    mergeMonths(projectMonths, phaseMonths);
  });

  byKey.set("PRJ", finalize(projectMonths));

  return { byKey, allMonths: Array.from(monthSet).sort() };
};

// Share of a node's cost that is phased into any month.
// Returns null when there is nothing to phase (planned === 0) — which is NOT
// the same as 0% and must not be flagged as a problem. Can legitimately exceed
// 100 when tasks are scheduled beyond their work quantity: never clamp it.
export const schedPct = (scheduled: number, planned: number): number | null =>
  planned > 0 ? (scheduled / planned) * 100 : null;

// Keys of every row that HAS children, i.e. every row an expand-all should
// open: phases and activities, never tasks. Used by the three 3M trees, which
// all start collapsed so the WBS reads as a summary until you drill in.
export const allExpandableKeys = (rows: TreeRow[]): string[] => {
  const keys: string[] = [];
  const walk = (list: TreeRow[]) => {
    (list || []).forEach((row) => {
      if (row.children && row.children.length) {
        keys.push(row.key);
        walk(row.children);
      }
    });
  };
  walk(rows);
  return keys;
};

// Cost per unit of scheduled output. Returns null when there is no output to
// divide by — a rate of "infinity" is worse than no rate at all.
export const perUnit = (cost: number, quantity: number): number | null =>
  quantity > 0 ? cost / quantity : null;
